'use client';

import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import DonationCard from './DonationCard';
import VolunteerOpportunities from './VolunteerOpportunities';
import PartnershipInquiry from './PartnershipInquiry';
import ImpactStats from './ImpactStats';
import TestimonialSection from './TestimonialSection';
import ReferralProgram from './ReferralProgram';
import Icon from '@/components/ui/AppIcon';

interface FormData {
  name: string;
  email: string;
  phone: string;
  message: string;
}

const GetInvolvedInteractive: React.FC = () => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [activeTab, setActiveTab] = useState<'donate' | 'volunteer' | 'partner'>('donate');
  const [showDonationModal, setShowDonationModal] = useState(false);
  const [showVolunteerModal, setShowVolunteerModal] = useState(false);
  const [showPartnerModal, setShowPartnerModal] = useState(false);
  const [selectedAmount, setSelectedAmount] = useState<number>(0);
  const [selectedOpportunity, setSelectedOpportunity] = useState<string>('');
  const [selectedPartnership, setSelectedPartnership] = useState<string>('');
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    phone: '',
    message: '',
  });
  const [formErrors, setFormErrors] = useState<Partial<FormData>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  if (!isHydrated) {
    return (
      <div className="min-h-screen bg-background">
        <div className="container mx-auto px-4 py-16">
          <div className="animate-pulse space-y-8">
            <div className="h-12 bg-muted rounded w-1/3 mx-auto"></div>
            <div className="h-64 bg-muted rounded"></div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div className="h-48 bg-muted rounded"></div>
              <div className="h-48 bg-muted rounded"></div>
              <div className="h-48 bg-muted rounded"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  const handleDonateClick = (amount: number) => {
    setSelectedAmount(amount);
    setShowDonationModal(true);
  };

  const handleVolunteerApply = (opportunityId: string) => {
    setSelectedOpportunity(opportunityId);
    setShowVolunteerModal(true);
  };

  const handlePartnershipInquiry = (partnershipType: string) => {
    setSelectedPartnership(partnershipType);
    setShowPartnerModal(true);
  };

  const validateForm = (): boolean => {
    const errors: Partial<FormData> = {};

    if (!formData.name.trim()) {
      errors.name = 'Name is required';
    }

    if (!formData.email.trim()) {
      errors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = 'Invalid email format';
    }

    if (!formData.phone.trim()) {
      errors.phone = 'Phone number is required';
    } else if (!/^\d{10}$/.test(formData.phone.replace(/\D/g, ''))) {
      errors.phone = 'Invalid phone number (10 digits required)';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    // Simulate API call
    await new Promise(resolve => setTimeout(resolve, 2000));

    setIsSubmitting(false);
    setSubmitSuccess(true);

    // Reset form after 3 seconds
    setTimeout(() => {
      setShowDonationModal(false);
      setShowVolunteerModal(false);
      setShowPartnerModal(false);
      setSubmitSuccess(false);
      setFormData({ name: '', email: '', phone: '', message: '' });
      setFormErrors({});
    }, 3000);
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (formErrors[name as keyof FormData]) {
      setFormErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const closeModal = () => {
    setShowDonationModal(false);
    setShowVolunteerModal(false);
    setShowPartnerModal(false);
    setFormData({ name: '', email: '', phone: '', message: '' });
    setFormErrors({});
    setSubmitSuccess(false);
  };

  const tabs = [
    { id: 'donate' as const, label: 'Donate', icon: 'HeartIcon' },
    { id: 'volunteer' as const, label: 'Volunteer', icon: 'HandRaisedIcon' },
    { id: 'partner' as const, label: 'Partner', icon: 'BuildingOfficeIcon' },
  ];

  return (
    <div className="space-y-12">
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-primary via-secondary to-accent rounded-2xl p-5 sm:p-8 lg:p-12 text-center">
        <h1 className="text-3xl lg:text-5xl font-heading font-bold text-white mb-4">
          Join Our Mission of Service
        </h1>
        <p className="text-lg text-white/90 max-w-3xl mx-auto mb-8">
          Every contribution, every hour volunteered, every partnership creates ripples of positive change in our community. Choose your path to make a difference.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-4">
          {tabs.map((tab) => (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-6 py-3 rounded-lg font-heading font-semibold transition-all duration-300 flex items-center space-x-2 ${
                activeTab === tab.id
                  ? 'bg-white text-primary shadow-cta'
                  : 'bg-white/20 text-white hover:bg-white/30'
              }`}
            >
              <Icon name={tab.icon as any} size={20} />
              <span>{tab.label}</span>
            </button>
          ))}
        </div>
      </div>

      {/* Impact Stats */}
      <ImpactStats />

      {/* Main Content Based on Active Tab */}
      <div className="space-y-12">
        {activeTab === 'donate' && (
          <motion.div
            id="donate"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="grid grid-cols-1 lg:grid-cols-3 gap-8 scroll-mt-24"
          >
            <div className="lg:col-span-2">
              <DonationCard onDonateClick={handleDonateClick} />
            </div>
            <div className="space-y-6">
              <div className="bg-card rounded-xl shadow-elevated p-6">
                <h3 className="text-lg font-heading font-bold text-foreground mb-4">Why Donate?</h3>
                <ul className="space-y-3">
                  {[
                    'Direct impact on families in need',
                    '100% transparent fund utilization',
                    'Tax benefits under 80G',
                    'Regular impact reports',
                    'Recognition in our community',
                  ].map((item, index) => (
                    <li key={index} className="flex items-start space-x-2 text-sm text-muted-foreground">
                      <Icon name="CheckCircleIcon" size={16} className="text-secondary mt-0.5 flex-shrink-0" variant="solid" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </div>
              <ReferralProgram />
            </div>
          </motion.div>
        )}

        {activeTab === 'volunteer' && (
          <motion.div
            id="volunteer"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-8 scroll-mt-24"
          >
            <VolunteerOpportunities onApplyClick={handleVolunteerApply} />
            <div className="bg-card rounded-xl shadow-elevated p-6 lg:p-8">
              <h3 className="text-xl font-heading font-bold text-foreground mb-4">Volunteer Benefits</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {[
                  { icon: 'AcademicCapIcon', title: 'Skill Development', desc: 'Learn new skills and gain experience' },
                  { icon: 'UserGroupIcon', title: 'Community Building', desc: 'Connect with like-minded people' },
                  { icon: 'TrophyIcon', title: 'Recognition', desc: 'Certificates and appreciation' },
                ].map((benefit, index) => (
                  <div key={index} className="flex items-start space-x-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Icon name={benefit.icon as any} size={20} className="text-primary" />
                    </div>
                    <div>
                      <h4 className="font-heading font-semibold text-foreground mb-1">{benefit.title}</h4>
                      <p className="text-sm text-muted-foreground">{benefit.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        )}

        {activeTab === 'partner' && (
          <motion.div
            id="partnership"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="space-y-8 scroll-mt-24"
          >
            <div id="csr" className="scroll-mt-24">
              <PartnershipInquiry onInquiryClick={handlePartnershipInquiry} />
            </div>
          </motion.div>
        )}
      </div>

      {/* Testimonials */}
      <TestimonialSection />

      {/* Donation Modal */}
      {showDonationModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl shadow-elevated max-w-md w-full p-6 relative">
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="XMarkIcon" size={24} />
            </button>

            {submitSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="CheckCircleIcon" size={32} className="text-secondary" variant="solid" />
                </div>
                <h3 className="text-2xl font-heading font-bold text-foreground mb-2">Thank You!</h3>
                <p className="text-muted-foreground">
                  Your donation of ₹{selectedAmount.toLocaleString('en-IN')} will make a real difference. You'll receive a confirmation email shortly.
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-heading font-bold text-foreground mb-2">Complete Your Donation</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Amount: ₹{selectedAmount.toLocaleString('en-IN')}
                </p>

                <form onSubmit={handleFormSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                        formErrors.name ? 'border-error' : 'border-border focus:border-primary'
                      }`}
                      placeholder="Enter your full name"
                    />
                    {formErrors.name && <p className="text-error text-xs mt-1">{formErrors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                        formErrors.email ? 'border-error' : 'border-border focus:border-primary'
                      }`}
                      placeholder="your.email@example.com"
                    />
                    {formErrors.email && <p className="text-error text-xs mt-1">{formErrors.email}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                        formErrors.phone ? 'border-error' : 'border-border focus:border-primary'
                      }`}
                      placeholder="10-digit mobile number"
                    />
                    {formErrors.phone && <p className="text-error text-xs mt-1">{formErrors.phone}</p>}
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-accent text-accent-foreground font-heading font-semibold py-3 rounded-lg hover:bg-accent/90 transition-all duration-300 shadow-subtle hover:shadow-cta disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                        <span>Processing...</span>
                      </>
                    ) : (
                      <>
                        <span>Proceed to Payment</span>
                        <Icon name="ArrowRightIcon" size={20} />
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Volunteer Modal */}
      {showVolunteerModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl shadow-elevated max-w-md w-full p-6 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="XMarkIcon" size={24} />
            </button>

            {submitSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="CheckCircleIcon" size={32} className="text-secondary" variant="solid" />
                </div>
                <h3 className="text-2xl font-heading font-bold text-foreground mb-2">Application Submitted!</h3>
                <p className="text-muted-foreground">
                  Thank you for your interest in volunteering. Our team will contact you within 48 hours with next steps.
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-heading font-bold text-foreground mb-2">Volunteer Application</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Join us in making a difference in our community
                </p>

                <form onSubmit={handleFormSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Full Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                        formErrors.name ? 'border-error' : 'border-border focus:border-primary'
                      }`}
                      placeholder="Enter your full name"
                    />
                    {formErrors.name && <p className="text-error text-xs mt-1">{formErrors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Email Address *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                        formErrors.email ? 'border-error' : 'border-border focus:border-primary'
                      }`}
                      placeholder="your.email@example.com"
                    />
                    {formErrors.email && <p className="text-error text-xs mt-1">{formErrors.email}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Phone Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                        formErrors.phone ? 'border-error' : 'border-border focus:border-primary'
                      }`}
                      placeholder="10-digit mobile number"
                    />
                    {formErrors.phone && <p className="text-error text-xs mt-1">{formErrors.phone}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Why do you want to volunteer?</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-4 py-3 border-2 border-border rounded-lg focus:border-primary focus:outline-none transition-colors resize-none"
                      placeholder="Tell us about your motivation and relevant experience..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-primary text-primary-foreground font-heading font-semibold py-3 rounded-lg hover:bg-primary/90 transition-all duration-300 shadow-subtle hover:shadow-cta disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <span>Submit Application</span>
                        <Icon name="ArrowRightIcon" size={20} />
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Partnership Modal */}
      {showPartnerModal && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-card rounded-xl shadow-elevated max-w-md w-full p-6 relative max-h-[90vh] overflow-y-auto">
            <button
              onClick={closeModal}
              className="absolute top-4 right-4 text-muted-foreground hover:text-foreground transition-colors"
            >
              <Icon name="XMarkIcon" size={24} />
            </button>

            {submitSuccess ? (
              <div className="text-center py-8">
                <div className="w-16 h-16 bg-secondary/10 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Icon name="CheckCircleIcon" size={32} className="text-secondary" variant="solid" />
                </div>
                <h3 className="text-2xl font-heading font-bold text-foreground mb-2">Inquiry Received!</h3>
                <p className="text-muted-foreground">
                  Thank you for your interest in partnering with us. Our partnerships team will reach out within 2 business days.
                </p>
              </div>
            ) : (
              <>
                <h3 className="text-2xl font-heading font-bold text-foreground mb-2">Partnership Inquiry</h3>
                <p className="text-sm text-muted-foreground mb-6">
                  Let's explore how we can create impact together
                </p>

                <form onSubmit={handleFormSubmit} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Organization Name *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                        formErrors.name ? 'border-error' : 'border-border focus:border-primary'
                      }`}
                      placeholder="Enter organization name"
                    />
                    {formErrors.name && <p className="text-error text-xs mt-1">{formErrors.name}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Contact Email *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                        formErrors.email ? 'border-error' : 'border-border focus:border-primary'
                      }`}
                      placeholder="contact@organization.com"
                    />
                    {formErrors.email && <p className="text-error text-xs mt-1">{formErrors.email}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Contact Number *</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className={`w-full px-4 py-3 border-2 rounded-lg focus:outline-none transition-colors ${
                        formErrors.phone ? 'border-error' : 'border-border focus:border-primary'
                      }`}
                      placeholder="10-digit contact number"
                    />
                    {formErrors.phone && <p className="text-error text-xs mt-1">{formErrors.phone}</p>}
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-foreground mb-2">Partnership Interest</label>
                    <textarea
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-4 py-3 border-2 border-border rounded-lg focus:border-primary focus:outline-none transition-colors resize-none"
                      placeholder="Describe your partnership goals and how you'd like to collaborate..."
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-supporting text-supporting-foreground font-heading font-semibold py-3 rounded-lg hover:bg-supporting/90 transition-all duration-300 shadow-subtle hover:shadow-cta disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center space-x-2"
                  >
                    {isSubmitting ? (
                      <>
                        <Icon name="ArrowPathIcon" size={20} className="animate-spin" />
                        <span>Submitting...</span>
                      </>
                    ) : (
                      <>
                        <span>Submit Inquiry</span>
                        <Icon name="ArrowRightIcon" size={20} />
                      </>
                    )}
                  </button>
                </form>
              </>
            )}
          </div>
        </div>
      )}

      {/* Sticky Action Buttons */}
      <div className="fixed bottom-6 right-6 flex flex-col space-y-3 z-40">
        <button
          onClick={() => {
            setActiveTab('donate');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          className="w-14 h-14 bg-accent text-accent-foreground rounded-full shadow-cta hover:scale-105 transition-all duration-300 flex items-center justify-center"
          aria-label="Quick Donate"
        >
          <Icon name="HeartIcon" size={24} variant="solid" />
        </button>
        <button
          onClick={() => {
            setActiveTab('volunteer');
            window.scrollTo({ top: 0, behavior: 'smooth' });
          }}
          className="w-14 h-14 bg-primary text-primary-foreground rounded-full shadow-cta hover:scale-105 transition-all duration-300 flex items-center justify-center"
          aria-label="Quick Volunteer"
        >
          <Icon name="HandRaisedIcon" size={24} variant="solid" />
        </button>
      </div>
    </div>
  );
};

export default GetInvolvedInteractive;